// UTM tagging for outbound Shrink Network links.
//
// The Shrink Network measures cross-network journeys with a single, consistent
// UTM shape so an entry site can be traced to the property a reader hops to
// next. This is the "Clarity Path Completion" north star metric: someone lands
// on shariqrefai.com, clicks through to shrinkMD or Shrinkopedia, and the
// destination site can attribute that session back here.
//
// UTM shape:
//   utm_source   = "shariqrefai" (this site)
//   utm_medium   = "network_journey"
//   utm_campaign = a short slug describing where the click came from
//                  (e.g. "network_footer", "founder_office_next_step",
//                  "home_shrink_atlas")
//
// Usage:
//   import { withNetworkUtm } from "@/lib/network";
//   <a href={withNetworkUtm("https://shrinkmd.com/", "home_hero_cta")} .../>
//
// Or use the <NetworkLink /> component which wraps this plus target/rel.

export const SOURCE_SITE = "shariqrefai";
export const NETWORK_MEDIUM = "network_journey";

// Hostnames of the Shrink Network sibling properties. Keep in sync with
// NetworkFooter.tsx and NetworkNextStep.tsx, which the build-time link
// conventions checker uses to derive the authoritative network host set.
export const NETWORK_HOSTS: ReadonlySet<string> = new Set([
  "shrinknetwork.com",
  "shrinkmd.com",
  "shrinkopedia.com",
  "shrinktionary.com",
  "shrinkdaily.com",
  "shrinkiatry.com",
  "shrinq.com",
  "beunstuck.app",
  "quitscrolling.app",
  "anxietyresource.org",
  "depressionresource.org",
  "anxietyresearch.org",
  "psychiatryrx.org",
]);

function hostOf(href: string): string | null {
  try {
    return new URL(href).hostname.replace(/^www\./i, "").toLowerCase();
  } catch {
    return null;
  }
}

export function isNetworkHref(href: string): boolean {
  const h = hostOf(href);
  return h !== null && NETWORK_HOSTS.has(h);
}

/**
 * Append the standard Shrink Network UTM tags to a network URL.
 * Preserves existing query params and hash. Never overwrites a UTM param the
 * caller already set. Returns the input unchanged for non-network URLs or
 * malformed inputs, so it is safe to call anywhere.
 */
export function withNetworkUtm(href: string, campaign: string): string {
  if (!isNetworkHref(href)) return href;
  try {
    const url = new URL(href);
    if (!url.searchParams.has("utm_source")) {
      url.searchParams.set("utm_source", SOURCE_SITE);
    }
    if (!url.searchParams.has("utm_medium")) {
      url.searchParams.set("utm_medium", NETWORK_MEDIUM);
    }
    if (campaign && !url.searchParams.has("utm_campaign")) {
      url.searchParams.set("utm_campaign", campaign);
    }
    return url.toString();
  } catch {
    return href;
  }
}
